import { UserRole } from '../types/user';

/**
 * Minimal shape needed to group AMC options. Both AmcListResponse (dashboardApi)
 * and the AMC filter option objects satisfy this.
 */
export interface GroupableAmc {
  id: string;
  name: string;
  isRTA?: boolean;
}

/**
 * AMC-type users (AMC admin / AMC user / AMC support) get the RTA vs Non-RTA
 * split, since mixing both lists is what confuses them. ADMIN and SUPPORT keep
 * the flat, ungrouped dropdown.
 */
export const isAmcTypeRole = (role?: string | null): boolean =>
  role === UserRole.AMC || role === UserRole.AMC_ADMIN || role === UserRole.AMC_SUPPORT;

export interface AmcGroup<T extends GroupableAmc> {
  /** Stable key / subheader label. */
  label: string;
  amcs: T[];
}

/**
 * Group AMCs for the dropdown into two sections for AMC-type users:
 *   1. "AMC"  — non-RTA AMCs (shown first)
 *   2. "RTA"  — RTA AMCs
 * Membership is decided solely by each AMC's isRTA flag. The user's own AMCs
 * (primaryIds) are sorted to the top of whichever group they fall in; the rest
 * sort by name. Empty groups are dropped so a subheader never renders without
 * items.
 *
 * @param amcs        the AMCs to display
 * @param primaryIds  the current user's own AMC ids (e.g. user.amcIds); used
 *                    only to order their own AMCs first within a group.
 */
export const groupAmcsByRta = <T extends GroupableAmc>(
  amcs: T[],
  primaryIds: string[] = []
): AmcGroup<T>[] => {
  // Names originate from API responses, so guard against null/undefined even
  // though the type declares `name: string` — one bad record must not crash the
  // whole dropdown render.
  const byName = (a: T, b: T) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base', numeric: true });
  const primarySet = new Set(primaryIds);
  // The user's own AMCs sort ahead of the rest within their group.
  const ordered = (list: T[]) =>
    [...list].sort((a, b) => {
      const aOwn = primarySet.has(a.id) ? 0 : 1;
      const bOwn = primarySet.has(b.id) ? 0 : 1;
      return aOwn - bOwn || byName(a, b);
    });

  // Grouping is driven purely by isRTA. Ownership only affects ordering.
  const amcGroup = ordered(amcs.filter((a) => !a.isRTA)); // "AMC" = non-RTA
  const rta = ordered(amcs.filter((a) => a.isRTA)); // "RTA"

  const groups: AmcGroup<T>[] = [];
  if (amcGroup.length) groups.push({ label: 'AMCs', amcs: amcGroup });
  if (rta.length) groups.push({ label: 'RTA PLATFORMS', amcs: rta });
  return groups;
};

/**
 * Secondary line shown under each dropdown option. Non-RTA AMCs have no
 * subtitle; RTA platforms read as a KFintech platform with name-specific
 * overrides (matched case-insensitively).
 */
const RTA_PLATFORM_LABELS: Record<string, string> = {
  iris: 'Kfin Distributor',
  kfinkart: 'Kfin Investor',
  'kfinkart v2': 'Kfin Investor',
};

export const getPlatformLabel = (amc: GroupableAmc): string => {
  if (!amc.isRTA) return '';
  const name = (amc.name ?? '').trim().toLowerCase();
  return RTA_PLATFORM_LABELS[name] ?? 'Kfin platform';
};
